import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Scale, Landmark, ShieldCheck, FileCheck2, Database, BrainCircuit, ClipboardCheck, Presentation, AlertCircle } from "lucide-react";
import SEO from "@/components/SEO";
import Reveal, { EASE } from "@/components/Reveal";
import ContactForm from "@/components/ContactForm";

const MotionLink = motion.create(Link);

const nonAdiAudiences = ["Real estate agents", "Lawyers & conveyancers", "Accountants", "Trust & company service providers", "AFSL & ACL holders"];

const nonAdiServices = [
  { icon: FileCheck2, title: "AML/CTF compliance support", desc: "Enrolment records, program documentation support, customer due diligence workflows, staff training materials and record-keeping systems — built and maintained so your obligations are operational, not theoretical." },
  { icon: Database, title: "Privacy & data governance support", desc: "Data mapping, retention policies and Privacy Act alignment, with systems that make your data handling demonstrable rather than assumed." },
  { icon: ClipboardCheck, title: "Complaints (IDR) & responsible-lending support", desc: "Internal dispute resolution workflows, registers and reporting for AFSL and ACL holders — documented, trackable and audit-ready." },
  { icon: Scale, title: "Fast-track for firms past their deadline", desc: "AUSTRAC Tranche 2 enrolment closed 29 July 2026. If your compliance start date has already passed, we run a prioritised engagement to get the essentials in place first." },
];

const adiCapabilities = [
  { icon: BrainCircuit, title: "Data-sovereign AI infrastructure", desc: "AI workloads hosted and operated within Australian jurisdiction, designed for institutions where data residency is non-negotiable." },
  { icon: ShieldCheck, title: "Compliance & regulatory support", desc: "Technology operations aligned to prudential obligations such as CPS 230 and CPS 234, working alongside your risk and compliance functions." },
  { icon: Landmark, title: "Managed security", desc: "Essential Eight-aligned monitoring, endpoint protection and security operations run for your environment." },
  { icon: BrainCircuit, title: "AI governance & model risk", desc: "Documentation, controls and monitoring frameworks for AI systems, so adoption is governed rather than improvised." },
  { icon: Presentation, title: "Ongoing assurance & board reporting", desc: "Evidence registers and reporting packs your executive and board can rely on, delivered on a regular cadence." },
];

const RegulatedIndustries = () => (
  <>
    <SEO
      title="Regulated Industry IT Services | UMI IT Services"
      description="AML/CTF compliance systems support for AUSTRAC Tranche 2 entities, and sovereign AI & regulatory technology for APRA-regulated mutual banks, credit unions and non-bank lenders."
    />

    <section className="relative overflow-hidden bg-[#0B2545] pt-28 pb-16 md:pt-36 md:pb-24">
      <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(55% 45% at 75% 25%, rgba(23,184,166,0.14), transparent 70%)" }} />
      <div className="pointer-events-none absolute inset-0 hero-grain opacity-[0.05]" />
      <div className="relative px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto max-w-3xl">
        <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}
          className="text-xs font-bold uppercase tracking-[0.2em] text-[#17B8A6]">Regulated industries</motion.p>
        <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.12, ease: EASE }}
          className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] text-white">
          Technology for organisations the <span className="text-[#17B8A6]">regulators watch.</span>
        </motion.h1>
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3, ease: EASE }}
          className="mt-6 text-lg text-slate-300 leading-relaxed">
          Separate from our small-business IT services, UMI runs two dedicated practices: compliance systems support for businesses newly regulated under AUSTRAC's Tranche 2 reforms, and sovereign AI &amp; regulatory technology for APRA-regulated institutions.
        </motion.p>
      </div>
    </section>

    {/* Non-ADI / Tranche 2 */}
    <section data-testid="regulated-non-adi-section" className="py-16 md:py-24 bg-white">
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#17B8A6]">AUSTRAC Tranche 2</p>
          <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0B2545] max-w-3xl">Newly regulated? The compliance clock is already running.</h2>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-3xl">
            Tranche 2 of the AML/CTF reforms brings real estate, legal, conveyancing, accounting and trust &amp; company service businesses into AUSTRAC's regime. Enrolment closed on 29 July 2026 — if that's you, your compliance start date has already passed, and the systems behind your program matter now.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <ul className="mt-6 flex flex-wrap gap-2.5">
            {nonAdiAudiences.map((a) => (
              <li key={a} className="rounded-full bg-[#F1F5F9] border border-slate-200 px-4 py-1.5 text-xs font-semibold text-slate-700">{a}</li>
            ))}
          </ul>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {nonAdiServices.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.07} className="h-full">
              <div className="h-full rounded-2xl border border-slate-200 bg-[#F8FAFC] p-6 sm:p-8">
                <div className="w-12 h-12 rounded-xl bg-[#0B2545] flex items-center justify-center">
                  <s.icon size={22} className="text-[#17B8A6]" aria-hidden="true" />
                </div>
                <h3 className="mt-5 text-lg sm:text-xl font-semibold text-[#0B2545]">{s.title}</h3>
                <p className="mt-2 text-base text-slate-600 leading-relaxed">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.15}>
          <div data-testid="regulated-disclaimer" className="mt-10 rounded-xl border border-amber-200 bg-amber-50 p-6 flex items-start gap-3 max-w-4xl">
            <AlertCircle size={20} className="mt-0.5 shrink-0 text-amber-600" aria-hidden="true" />
            <p className="text-sm text-slate-700 leading-relaxed">
              <strong className="text-[#0B2545]">Our role, stated plainly:</strong> UMI builds, documents and maintains the systems behind your compliance program, and supports your compliance function day to day. We do not certify or guarantee the adequacy of your AML/CTF program — that responsibility always remains with your own compliance officer, together with your external AML/CTF advisor.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <MotionLink to="/contact" data-testid="regulated-cta-non-adi"
            whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}
            className="mt-8 inline-flex items-center gap-2 bg-[#17B8A6] hover:bg-[#0D9488] text-white font-semibold px-7 py-3.5 rounded-lg transition-colors duration-200">
            Book a Compliance Systems Conversation <ArrowRight size={18} aria-hidden="true" />
          </MotionLink>
        </Reveal>
      </div>
    </section>

    {/* ADI / APRA-regulated */}
    <section data-testid="regulated-adi-section" className="relative overflow-hidden py-16 md:py-24 bg-[#07182E]">
      <div className="pointer-events-none absolute inset-0 hero-grain opacity-[0.05]" />
      <div className="relative px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#17B8A6]">APRA-regulated institutions</p>
          <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white max-w-3xl">Sovereign AI &amp; regulatory technology for ADIs</h2>
          <p className="mt-5 text-lg text-slate-300 leading-relaxed max-w-3xl">
            For mutual banks, credit unions and smaller non-bank lenders exploring sovereign AI and stronger regulatory technology, UMI offers an early, engineering-led conversation. We are deliberately selective in this segment — the goal is the right partnerships, not the most logos.
          </p>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {adiCapabilities.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.06} className="h-full">
              <div className="h-full rounded-2xl border border-white/10 bg-white/5 p-6 sm:p-8">
                <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                  <c.icon size={22} className="text-[#17B8A6]" aria-hidden="true" />
                </div>
                <h3 className="mt-5 text-lg font-semibold text-white">{c.title}</h3>
                <p className="mt-2 text-base text-slate-300 leading-relaxed">{c.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.15}>
          <p className="mt-10 text-sm text-slate-400 leading-relaxed max-w-3xl">
            All regulatory technology work is delivered alongside your institution's compliance, risk and legal functions. Accountability for prudential compliance remains with your institution at all times.
          </p>
        </Reveal>
        <Reveal delay={0.2}>
          <MotionLink to="/contact" data-testid="regulated-cta-adi"
            whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}
            className="mt-6 inline-flex items-center gap-2 border-2 border-white/25 text-white hover:bg-white/10 font-semibold px-7 py-3.5 rounded-lg transition-colors duration-200">
            Start a Conversation <ArrowRight size={18} aria-hidden="true" />
          </MotionLink>
        </Reveal>
      </div>
    </section>

    {/* Enquiry form */}
    <section className="relative overflow-hidden bg-[#0B2545] py-16 md:py-24">
      <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(50% 60% at 50% 100%, rgba(23,184,166,0.14), transparent 70%)" }} />
      <div className="relative px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center">
        <Reveal>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">Start a confidential conversation</h2>
          <p className="mt-4 text-lg text-slate-300 max-w-2xl mx-auto">Tell us a little about your organisation and obligations — an Adelaide-based specialist will get back to you personally.</p>
        </Reveal>
        <Reveal delay={0.12}>
          <div data-testid="regulated-form" className="mt-10 max-w-2xl mx-auto rounded-2xl bg-white p-6 sm:p-8 text-left shadow-2xl">
            <ContactForm formLocation="regulated_industries" compact />
          </div>
        </Reveal>
      </div>
    </section>
  </>
);

export default RegulatedIndustries;
